import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import { mockAppointments, mockUsers } from '../mockData';
import PatientProfileForm from '../components/PatientProfileForm';
import TelemedicineConsentModal from '../components/TelemedicineConsentModal';
import ClinicalFilesPanel from '../components/ClinicalFilesPanel';
import PreTriagePanel from '../components/PreTriagePanel';
import PatientDocumentsPanel from '../components/PatientDocumentsPanel';
import { normalizePatientProfile, validatePatientProfile } from '../clinical/patientProfile';
import { APPOINTMENT_STATUSES, getAppointmentStatusLabel, isUpcomingAppointment, normalizeAppointmentStatus } from '../clinical/appointmentStatus';
import { alarmSignOptions, emptyPreTriageForm, getTriagePriorityLabel, classifyPreTriage } from '../clinical/preTriage';
import { auditFetch } from '../utils/auditFetch';
import '../styles/Dashboard.css';

const PatientDashboard = ({ user, updateUser }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [appointments, setAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [profile, setProfile] = useState(() => normalizePatientProfile(user));
  const [profileErrors, setProfileErrors] = useState([]);
  const [saveMessage, setSaveMessage] = useState('');
  const [selectedDoctorId, setSelectedDoctorId] = useState(mockUsers.doctors.find((doctor) => doctor.isOnline)?.id || mockUsers.doctors[0]?.id);
  const [appointmentRequest, setAppointmentRequest] = useState({
    date: '',
    time: '',
    reason: '',
  });
  const [preTriageRequest, setPreTriageRequest] = useState(emptyPreTriageForm);
  const [consentsByAppointment, setConsentsByAppointment] = useState({});
  const [pendingConsentAppointment, setPendingConsentAppointment] = useState(null);
  const [consentNextAction, setConsentNextAction] = useState('prepare');
  const [consentError, setConsentError] = useState('');
  const [isSavingConsent, setIsSavingConsent] = useState(false);
  const [activePatientSection, setActivePatientSection] = useState('inicio');
  const notifiedAppointmentsRef = useRef(new Set());
  const navigate = useNavigate();
  const auditActor = useMemo(() => ({
    id: user.id,
    role: user.role,
    name: user.name,
  }), [user.id, user.role, user.name]);

  useEffect(() => {
    const patientId = user.id;
    const baseUser = mockUsers.patients.find((patient) => patient.id === patientId) || { id: patientId, role: 'patient' };
    setProfile(normalizePatientProfile(baseUser));

    if (user.demoMode) {
      auditFetch(`${backendUrl}/api/demo/presentation`, {}, auditActor)
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Demo not found'))))
        .then((data) => {
          const demoPatient = (data.users?.patients || []).find((patient) => patient.id === patientId) || baseUser;
          const patientAppointments = (data.appointments || []).filter((apt) => apt.patientId === patientId);
          const nextProfile = normalizePatientProfile({ ...baseUser, ...demoPatient, demoMode: true });
          setProfile(nextProfile);
          updateUser(nextProfile);
          setAppointments(patientAppointments);
          setUpcomingAppointments(patientAppointments.filter(apt => isUpcomingAppointment(apt.status)));
          setMedicalRecords((data.medicalRecords || []).filter((record) => record.patientId === patientId));
          setConsentsByAppointment(Object.fromEntries(patientAppointments.map((appointment) => [
            appointment.id,
            { status: 'accepted', accepted: true, demoMode: true },
          ])));
        })
        .catch((error) => {
          console.error('Error loading patient demo:', error);
          const patientAppointments = mockAppointments.filter(apt => apt.patientId === patientId);
          setAppointments(patientAppointments);
          setUpcomingAppointments(patientAppointments.filter(apt => isUpcomingAppointment(apt.status)));
        });
      return;
    }

    auditFetch(`${backendUrl}/api/agenda/appointments?patientId=${encodeURIComponent(patientId)}`, {}, auditActor)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Appointments not found'))))
      .then((data) => {
        const storedAppointments = data.appointments || [];
        const patientAppointments = storedAppointments.length > 0
          ? storedAppointments
          : mockAppointments.filter(apt => apt.patientId === patientId);
        setAppointments(patientAppointments);
        setUpcomingAppointments(patientAppointments.filter(apt => isUpcomingAppointment(apt.status)));

        return Promise.all(patientAppointments.map((appointment) => (
          auditFetch(`${backendUrl}/api/consents/telemedicine/${appointment.id}?patientId=${patientId}`, {}, auditActor)
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => [appointment.id, data?.consent || null])
            .catch(() => [appointment.id, null])
        )));
      })
      .then((entries) => {
        setConsentsByAppointment(Object.fromEntries(entries || []));
      })
      .catch(() => {
        const patientAppointments = mockAppointments.filter(apt => apt.patientId === patientId);
        setAppointments(patientAppointments);
        setUpcomingAppointments(patientAppointments.filter(apt => isUpcomingAppointment(apt.status)));
      });

    auditFetch(`${backendUrl}/api/patients/${patientId}`, {}, auditActor)
      .then((res) => (res.ok ? res.json() : { profile: null }))
      .then((data) => {
        const nextProfile = normalizePatientProfile({ ...baseUser, ...data.profile });
        setProfile(nextProfile);
        updateUser(nextProfile);
      })
      .catch(() => setProfile(normalizePatientProfile(baseUser)));

    auditFetch(`${backendUrl}/api/consultations/records/${user.id}`, {}, auditActor)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setMedicalRecords(data))
      .catch((e) => console.error('Error loading medical records:', e));
  }, [user.id, user.demoMode, updateUser, auditActor, backendUrl]);

  const sortedAppointments = useMemo(() => (
    [...appointments].sort((first, second) => new Date(first.datetime) - new Date(second.datetime))
  ), [appointments]);
  const sortedUpcomingAppointments = useMemo(() => (
    [...upcomingAppointments].sort((first, second) => new Date(first.datetime) - new Date(second.datetime))
  ), [upcomingAppointments]);
  const currentAppointment = sortedAppointments.find((appointment) => (
    ['en_atencion', 'sala_espera', 'active'].includes(normalizeAppointmentStatus(appointment.status))
  )) || sortedUpcomingAppointments[0] || sortedAppointments[0] || null;
  const nextAppointment = currentAppointment;
  const activePlanLabel = profile.serviceActive ? 'Servicio activo' : 'Servicio inactivo';
  const selectedDoctor = mockUsers.doctors.find((doctor) => doctor.id === selectedDoctorId) || mockUsers.doctors[0];
  const activeDoctors = mockUsers.doctors.filter((doctor) => doctor.isOnline || doctor.onDuty);
  const requestPriority = classifyPreTriage(preTriageRequest);

  const hasAcceptedConsent = useCallback((appointmentId) => (
    consentsByAppointment[appointmentId]?.status === 'accepted'
    || consentsByAppointment[appointmentId]?.accepted === true
  ), [consentsByAppointment]);

  const enterConsultation = useCallback((appointment) => {
    navigate(`/consultation/${appointment.id}`, {
      state: { appointment: { ...appointment, patient: profile }, user }
    });
  }, [navigate, profile, user]);

  const handleJoinConsultation = useCallback((appointment) => {
    if (!appointment) return;
    if (!hasAcceptedConsent(appointment.id)) {
      setConsentError('');
      setConsentNextAction('join');
      setPendingConsentAppointment({ ...appointment, patient: profile });
      return;
    }
    enterConsultation(appointment);
  }, [enterConsultation, hasAcceptedConsent, profile]);

  const handlePrepareConsent = useCallback((appointment) => {
    if (!appointment) return;
    setConsentError('');
    setConsentNextAction('prepare');
    setPendingConsentAppointment({ ...appointment, patient: profile });
  }, [profile]);

  useEffect(() => {
    if (user.demoMode) return undefined;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const dashboardSocket = io(backendUrl || undefined);

    dashboardSocket.on('connect', () => {
      dashboardSocket.emit('user:online', {
        userId: user.id,
        role: user.role,
      });
    });

    dashboardSocket.on('consultation:participant-waiting', (data) => {
      if (data.role !== 'doctor') return;

      const appointment = appointments.find(
        (apt) => apt.id === data.appointmentId && apt.patientId === user.id
      ) || mockAppointments.find(
        (apt) => apt.id === data.appointmentId && apt.patientId === user.id
      );
      if (!appointment || notifiedAppointmentsRef.current.has(appointment.id)) return;

      notifiedAppointmentsRef.current.add(appointment.id);
      const doctorName = appointment.doctor?.name || 'Tu médico';
      const shouldEnter = window.confirm(`${doctorName} inició la consulta. ¿Deseas entrar ahora?`);

      if (shouldEnter) {
        handleJoinConsultation(appointment);
      }
    });

    return () => dashboardSocket.disconnect();
  }, [appointments, handleJoinConsultation, user]);

  const handleAcceptConsent = async ({ checks, version }) => {
    if (!pendingConsentAppointment) return;
    setIsSavingConsent(true);
    setConsentError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await auditFetch(`${backendUrl}/api/consents/telemedicine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointmentId: pendingConsentAppointment.id,
          patientId: user.id,
          patientName: profile.name || user.name,
          userId: user.id,
          accepted: true,
          checks,
          version,
        }),
      }, user);

      if (!response.ok) throw new Error('No se pudo registrar el consentimiento');
      const data = await response.json();
      setConsentsByAppointment((current) => ({
        ...current,
        [pendingConsentAppointment.id]: data.consent,
      }));
      const appointmentToEnter = pendingConsentAppointment;
      setPendingConsentAppointment(null);
      if (consentNextAction === 'join') {
        enterConsultation(appointmentToEnter);
      } else {
        setSaveMessage('Consentimiento aceptado. La cita queda lista para teleconsulta.');
        setTimeout(() => setSaveMessage(''), 3500);
      }
    } catch (error) {
      console.error('Error saving consent:', error);
      setConsentError('No se pudo registrar el consentimiento. Intenta nuevamente.');
    } finally {
      setIsSavingConsent(false);
    }
  };

  const handleSaveProfile = async () => {
    const errors = validatePatientProfile(profile);
    setProfileErrors(errors);
    if (errors.length > 0) {
      setSaveMessage('Completa los campos obligatorios de la ficha.');
      setTimeout(() => setSaveMessage(''), 3500);
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await auditFetch(`${backendUrl}/api/patients/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile }),
      }, user);
      if (!response.ok) throw new Error('No se pudo guardar la ficha');
      const data = await response.json();
      const nextProfile = normalizePatientProfile(data.profile);
      setProfile(nextProfile);
      updateUser(nextProfile);
      setSaveMessage('Ficha del paciente guardada correctamente');
    } catch (error) {
      console.error('Error saving patient profile:', error);
      setSaveMessage('No se pudo guardar la ficha del paciente.');
    }
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const createPatientAppointment = async ({ immediate = false } = {}) => {
    if (!selectedDoctor) return null;

    const appointmentDate = immediate
      ? new Date()
      : new Date(`${appointmentRequest.date}T${appointmentRequest.time || '09:00'}`);

    if (!immediate && (!appointmentRequest.date || Number.isNaN(appointmentDate.getTime()))) {
      setSaveMessage('Selecciona una fecha valida para solicitar la cita.');
      setTimeout(() => setSaveMessage(''), 3500);
      return null;
    }
    if (!preTriageRequest.mainSymptoms.trim() || !preTriageRequest.evolutionTime.trim()) {
      setSaveMessage('Completa el pretriaje antes de solicitar la atencion.');
      setTimeout(() => setSaveMessage(''), 3500);
      return null;
    }

    const appointmentPayload = {
      doctor: selectedDoctor,
      patient: profile,
      datetime: appointmentDate.toISOString(),
      durationMinutes: 30,
      reason: appointmentRequest.reason || (immediate ? 'Llamada inmediata desde portal' : 'Solicitud de cita desde portal'),
      specialty: selectedDoctor.specialty,
    };

    try {
      const response = await auditFetch(`${backendUrl}/api/agenda/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appointmentPayload),
      }, auditActor);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo registrar la cita.');

      let newAppointment = data.appointment;
      if (immediate) {
        const statusResponse = await auditFetch(`${backendUrl}/api/agenda/appointments/${newAppointment.id}/status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: APPOINTMENT_STATUSES.inProgress }),
        }, auditActor);
        const statusData = await statusResponse.json();
        if (statusResponse.ok) {
          newAppointment = statusData.appointment;
        }
      }

      const preTriageResponse = await auditFetch(`${backendUrl}/api/pretriage/${newAppointment.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preTriageRequest),
      }, auditActor);
      const preTriageData = await preTriageResponse.json();
      if (!preTriageResponse.ok) throw new Error(preTriageData.message || 'No se pudo guardar el pretriaje.');
      if (preTriageResponse.ok) {
        newAppointment = preTriageData.appointment;
      }

      const nextAppointments = [...appointments, newAppointment];
      setAppointments(nextAppointments);
      setUpcomingAppointments(nextAppointments.filter((apt) => isUpcomingAppointment(apt.status)));
      setSaveMessage(immediate
        ? `Conectando con el medico seleccionado. Prioridad ${getTriagePriorityLabel(preTriageData.preTriage?.priority || requestPriority)}.`
        : `Solicitud de cita registrada. Prioridad ${getTriagePriorityLabel(preTriageData.preTriage?.priority || requestPriority)}.`);
      setAppointmentRequest({ date: '', time: '', reason: '' });
      setPreTriageRequest(emptyPreTriageForm);
      return newAppointment;
    } catch (error) {
      console.error('Error creating patient appointment:', error);
      setSaveMessage(error.message || 'No se pudo registrar la cita.');
      setTimeout(() => setSaveMessage(''), 3500);
      return null;
    }
  };

  const handleImmediateCall = async () => {
    if (!selectedDoctor?.isOnline) {
      setSaveMessage('Este medico no esta en linea para llamada inmediata.');
      setTimeout(() => setSaveMessage(''), 3500);
      return;
    }

    const appointment = await createPatientAppointment({ immediate: true });
    if (appointment) {
      handleJoinConsultation(appointment);
    }
  };

  const handleRequestAppointment = async () => {
    await createPatientAppointment({ immediate: false });
  };

  const updatePreTriageAlarm = (key, checked) => {
    setPreTriageRequest((current) => ({
      ...current,
      alarmSigns: {
        ...(current.alarmSigns || {}),
        [key]: checked,
      },
    }));
  };

  const patientSections = [
    ['inicio', 'Inicio'],
    ['citas', 'Mis citas'],
    ['solicitar', 'Nueva cita'],
    ['documentos', 'Documentos'],
    ['pretriaje', 'Pretriaje'],
    ['perfil', 'Mis datos'],
    ['registros', 'Indicaciones'],
  ];

  const renderAppointmentCard = (apt, featured = false) => (
    <div key={apt.id} className={`appointment-card patient-card ${featured ? 'patient-current-card' : ''}`}>
      <div className="apt-header">
        <h3 className="doctor-name">{apt.doctor?.name || 'Medico'}</h3>
        <span className={`status-badge status-${normalizeAppointmentStatus(apt.status)}`}>
          {getAppointmentStatusLabel(apt.status)}
        </span>
      </div>
      <div className="apt-details">
        <p className="apt-specialty">
          <strong>Especialidad:</strong> {apt.doctor?.specialty || apt.specialty || 'General'}
        </p>
        <p className="apt-reason">
          <strong>Motivo:</strong> {apt.reason || 'Consulta por telemedicina'}
        </p>
        <p className="apt-time">
          <strong>Fecha y hora:</strong> {new Date(apt.datetime).toLocaleString('es-ES')}
        </p>
        <p className="apt-time">
          <strong>Consentimiento:</strong>{' '}
          <span className={hasAcceptedConsent(apt.id) ? 'status-online' : 'status-offline'}>
            {hasAcceptedConsent(apt.id) ? 'Aceptado' : 'Pendiente'}
          </span>
        </p>
        <p className="apt-time">
          <strong>Pretriaje:</strong>{' '}
          <span className={`triage-priority triage-priority-${apt.preTriage?.priority || 'baja'}`}>
            {apt.preTriage ? getTriagePriorityLabel(apt.preTriage.priority) : 'Pendiente'}
          </span>
        </p>
      </div>
      <div className="apt-actions">
        {!hasAcceptedConsent(apt.id) && (
          <button
            className="btn btn-secondary"
            onClick={() => handlePrepareConsent(apt)}
            type="button"
          >
            Aceptar consentimiento
          </button>
        )}
        <button
          className="btn btn-success"
          onClick={() => handleJoinConsultation(apt)}
          type="button"
        >
          Entrar a consulta
        </button>
      </div>
    </div>
  );

  const renderCurrentAppointment = () => (
    <section className="patient-current-appointment">
      <div className="patient-current-copy">
        <span>{currentAppointment ? getAppointmentStatusLabel(currentAppointment.status) : activePlanLabel}</span>
        <h2>{currentAppointment ? 'Tu cita más importante ahora' : 'No tienes una cita activa'}</h2>
        <p>
          {currentAppointment
            ? 'Revisa consentimiento, pretriaje y entra a la sala cuando estés listo.'
            : 'Puedes solicitar una cita o preparar tus datos para la próxima atención.'}
        </p>
      </div>
      {currentAppointment ? (
        renderAppointmentCard(currentAppointment, true)
      ) : (
        <div className="patient-current-empty">
          <strong>Sin cita en curso</strong>
          <button className="btn btn-primary" type="button" onClick={() => setActivePatientSection('solicitar')}>
            Solicitar cita
          </button>
        </div>
      )}
    </section>
  );

  const renderRequestSection = () => (
    <section className="patient-section-card">
      <div className="section-header">
        <h2>Solicitar atención</h2>
      </div>

      <div className="doctor-availability-grid">
        {activeDoctors.map((doctor) => (
          <button
            key={doctor.id}
            className={`doctor-availability-card ${selectedDoctorId === doctor.id ? 'selected' : ''}`}
            onClick={() => setSelectedDoctorId(doctor.id)}
            type="button"
          >
            <div>
              <h3>{doctor.name}</h3>
              <p>{doctor.specialty}</p>
              <small>{doctor.onDuty ? 'De turno' : 'Fuera de turno'} · {doctor.isOnline ? 'En linea' : `Disponible ${doctor.nextAvailable}`}</small>
            </div>
            <span className={`availability-dot ${doctor.isOnline ? 'online' : 'standby'}`} />
          </button>
        ))}
      </div>

      <div className="appointment-request-panel">
        <div>
          <h3>Nueva cita</h3>
          <p>Medico seleccionado: <strong>{selectedDoctor?.name}</strong></p>
        </div>
        <div className="request-grid">
          <label>
            Fecha
            <input
              type="date"
              value={appointmentRequest.date}
              onChange={(event) => setAppointmentRequest((current) => ({ ...current, date: event.target.value }))}
            />
          </label>
          <label>
            Hora
            <input
              type="time"
              value={appointmentRequest.time}
              onChange={(event) => setAppointmentRequest((current) => ({ ...current, time: event.target.value }))}
            />
          </label>
          <label>
            Motivo
            <input
              type="text"
              value={appointmentRequest.reason}
              onChange={(event) => setAppointmentRequest((current) => ({ ...current, reason: event.target.value }))}
              placeholder="Describe brevemente tu necesidad"
            />
          </label>
        </div>
        <div className="pretriage-panel">
          <div className="pretriage-header">
            <div>
              <h3>Pretriaje antes de la teleconsulta</h3>
              <p>Completa estos datos para clasificar la prioridad inicial.</p>
            </div>
            <span className={`triage-priority triage-priority-${requestPriority}`}>
              {getTriagePriorityLabel(requestPriority)}
            </span>
          </div>
          <div className="pretriage-form">
            <label>
              <span>Sintomas principales</span>
              <textarea
                value={preTriageRequest.mainSymptoms}
                onChange={(event) => setPreTriageRequest((current) => ({ ...current, mainSymptoms: event.target.value }))}
                placeholder="Ej. tos, fiebre, dolor abdominal, mareo"
              />
            </label>
            <label>
              <span>Tiempo de evolucion</span>
              <input
                value={preTriageRequest.evolutionTime}
                onChange={(event) => setPreTriageRequest((current) => ({ ...current, evolutionTime: event.target.value }))}
                placeholder="Ej. 2 dias, 1 semana, inicio hoy"
              />
            </label>
            <div className="pretriage-alarm-grid">
              {alarmSignOptions.map(([key, label]) => (
                <label key={key} className="pretriage-alarm-option">
                  <input
                    type="checkbox"
                    checked={Boolean(preTriageRequest.alarmSigns?.[key])}
                    onChange={(event) => updatePreTriageAlarm(key, event.target.checked)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <label>
              <span>Antecedentes relevantes</span>
              <textarea
                value={preTriageRequest.relevantHistory}
                onChange={(event) => setPreTriageRequest((current) => ({ ...current, relevantHistory: event.target.value }))}
                placeholder="Ej. embarazo, diabetes, hipertension, asma, alergias, medicamentos"
              />
            </label>
          </div>
        </div>
        <div className="request-actions">
          <button className="btn btn-primary" onClick={handleRequestAppointment} type="button">
            Solicitar cita
          </button>
          <button className="btn btn-success" onClick={handleImmediateCall} disabled={!selectedDoctor?.isOnline} type="button">
            Llamada inmediata
          </button>
        </div>
      </div>
    </section>
  );

  const renderAppointmentsSection = () => (
    <section className="patient-section-card">
      <div className="section-header">
        <h2>Mis citas</h2>
      </div>
      {sortedAppointments.length === 0 ? (
        <div className="empty-state">
          <p>No tienes citas programadas</p>
          <small>Solicita una cita cuando necesites atención.</small>
        </div>
      ) : (
        <div className="appointments-grid patient-appointments-list">
          {sortedAppointments.map((apt) => renderAppointmentCard(apt))}
        </div>
      )}
    </section>
  );

  const renderProfileSection = () => (
    <section className="patient-section-card patient-profile-card">
      <PatientProfileForm
        profile={profile}
        onChange={setProfile}
        onSave={handleSaveProfile}
        errors={profileErrors}
        saveMessage={saveMessage}
        compact
      />
      <div className="patient-profile-service">
        <p>
          <strong>Servicio:</strong>{' '}
          <span className={profile.serviceActive ? 'status-online' : 'status-offline'}>
            {profile.serviceActive ? 'Activo' : 'Inactivo'}
          </span>
        </p>
        <button className="btn btn-primary full-width" onClick={handleSaveProfile} type="button">
          Guardar datos
        </button>
      </div>
      {saveMessage && <p className="save-message">{saveMessage}</p>}
    </section>
  );

  const renderPreTriageSection = () => (
    <section className="patient-section-card">
      {nextAppointment ? (
        <PreTriagePanel
          appointment={nextAppointment}
          user={user}
          editable
          compact
          onSaved={(data) => {
            const nextAppointments = appointments.map((appointment) => (
              appointment.id === data.appointment.id ? data.appointment : appointment
            ));
            setAppointments(nextAppointments);
            setUpcomingAppointments(nextAppointments.filter((apt) => isUpcomingAppointment(apt.status)));
          }}
        />
      ) : (
        <div className="empty-state">
          <p>No hay una cita para completar pretriaje.</p>
          <small>Solicita una cita para registrar síntomas y prioridad.</small>
        </div>
      )}
    </section>
  );

  const renderDocumentsSection = () => (
    <section className="patient-section-card">
      {nextAppointment && (
        <ClinicalFilesPanel
          appointmentId={nextAppointment.id}
          patientId={user.id}
          patientName={profile.name || user.name}
          user={user}
          canUpload
          compact
        />
      )}
      <PatientDocumentsPanel
        appointments={appointments}
        medicalRecords={medicalRecords}
        user={user}
      />
    </section>
  );

  const renderRecordsSection = () => (
    <section className="patient-section-card">
      <div className="section-header">
        <h2>Indicaciones y registros</h2>
      </div>
      {medicalRecords.length === 0 ? (
        <div className="empty-state">
          <p>No hay registros médicos disponibles</p>
          <small>Recetas, órdenes e indicaciones aparecerán después de la consulta.</small>
        </div>
      ) : (
        <div className="patient-record-list">
          {medicalRecords.map((record) => {
            const hcu002 = record.hcu002 || {};
            const diagnoses = hcu002.diagnoses || [];
            const primaryDiagnosis = diagnoses.find((diagnosis) => diagnosis.description || diagnosis.cie10Code);
            const treatmentPlan = hcu002.treatmentPlan || {};
            const followUp = hcu002.clinicalFollowUp || {};

            return (
              <article key={record.id} className="patient-record-card">
                <strong>
                  {primaryDiagnosis
                    ? `Principal: ${primaryDiagnosis.cie10Code || ''} ${primaryDiagnosis.description || ''}`.trim()
                    : record.diagnosis || 'Registro clinico'}
                </strong>
                {hcu002.consultationReason?.description && <p><strong>Motivo:</strong> {hcu002.consultationReason.description}</p>}
                {(treatmentPlan.therapeutic || record.evolution) && <p><strong>Plan:</strong> {treatmentPlan.therapeutic || record.evolution}</p>}
                {followUp.evolution && <p><strong>Evolución:</strong> {followUp.evolution}</p>}
                {followUp.requiresNextAppointment && (
                  <p><strong>Próxima cita:</strong> {followUp.nextAppointmentDate} {followUp.nextAppointmentTime} · {followUp.nextAppointmentReason}</p>
                )}
                {treatmentPlan.recommendations && <p><strong>Recomendaciones:</strong> {treatmentPlan.recommendations}</p>}
                <small>{new Date(record.updatedAt || record.savedAt).toLocaleDateString('es-ES')}</small>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );

  const renderHomeSection = () => (
    <div className="patient-home-grid">
      <button className="patient-home-action" type="button" onClick={() => setActivePatientSection('citas')}>
        <strong>{appointments.length}</strong>
        <span>Citas registradas</span>
      </button>
      <button className="patient-home-action" type="button" onClick={() => setActivePatientSection('documentos')}>
        <strong>{medicalRecords.length}</strong>
        <span>Registros e indicaciones</span>
      </button>
      <button className="patient-home-action" type="button" onClick={() => setActivePatientSection('perfil')}>
        <strong>{profile.serviceActive ? 'Activo' : 'Inactivo'}</strong>
        <span>Estado del servicio</span>
      </button>
      <section className="patient-section-card patient-help-card">
        <h2>Antes de la consulta</h2>
        <ul>
          <li>Acepta el consentimiento de tu cita.</li>
          <li>Completa el pretriaje si está pendiente.</li>
          <li>Ten cámara y micrófono habilitados.</li>
          <li>Sube documentos o exámenes si el médico los necesita.</li>
        </ul>
      </section>
    </div>
  );

  const renderActivePatientSection = () => {
    if (activePatientSection === 'citas') return renderAppointmentsSection();
    if (activePatientSection === 'solicitar') return renderRequestSection();
    if (activePatientSection === 'documentos') return renderDocumentsSection();
    if (activePatientSection === 'pretriaje') return renderPreTriageSection();
    if (activePatientSection === 'perfil') return renderProfileSection();
    if (activePatientSection === 'registros') return renderRecordsSection();
    return renderHomeSection();
  };

  return (
    <div className="dashboard patient-dashboard">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>Portal del Paciente</h1>
          <p className="subtitle">Bienvenido, {user.name} · {activePlanLabel}</p>
        </div>

        <div className="patient-portal-layout">
          <nav className="patient-portal-menu" aria-label="Menu del paciente">
            {patientSections.map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={activePatientSection === key ? 'active' : ''}
                onClick={() => setActivePatientSection(key)}
              >
                {label}
              </button>
            ))}
          </nav>

          <main className="patient-portal-main">
            {renderCurrentAppointment()}
            {renderActivePatientSection()}
          </main>
        </div>
      </div>
      {pendingConsentAppointment && (
        <TelemedicineConsentModal
          appointment={pendingConsentAppointment}
          patient={profile}
          onAccept={handleAcceptConsent}
          onCancel={() => {
            setPendingConsentAppointment(null);
            setConsentNextAction('prepare');
          }}
          isSaving={isSavingConsent}
          error={consentError}
        />
      )}
    </div>
  );
};

export default PatientDashboard;
